/**
 * section-contact-form-uploader.js
 * -------------------------------------------------------------
 * Shopify コンタクトフォーム用 画像アップローダー（Cloudinary 連携）
 *
 * 概要:
 *  - 標準の {% form 'contact' %} を維持したまま、画像を Cloudinary に
 *    「未署名アップロード（unsigned upload preset）」でアップロードし、
 *    取得した画像URLを隠しフィールドに書き込む。
 *  - フォーム送信時、そのURLが contact[...] として通知メールに載る。
 *  - スマホ撮影 / ファイル選択の両対応、最大N枚、プレビュー、容量チェック、
 *    自動リサイズ（EXIF回転対応）、送信中の送信ボタン制御まで担当。
 *
 * 設定は section-contact-form.liquid 側の data-* 属性から受け取る。
 * 依存ライブラリなし（Vanilla JS）。
 * -------------------------------------------------------------
 */
(function () {
  'use strict';
 
  /** ルート要素ごとに初期化（テーマエディターでの再描画にも対応） */
  function initAll() {
    document
      .querySelectorAll('[data-contact-uploader]:not([data-uploader-ready])')
      .forEach(function (root) {
        try {
          new ContactImageUploader(root);
          root.setAttribute('data-uploader-ready', 'true');
        } catch (e) {
          console.error('[contact-uploader] init failed:', e);
        }
      });
  }
 
  function ContactImageUploader(root) {
    this.root = root;
 
    // ---- 設定（Liquid の data-* から） ----
    this.cloudName = root.getAttribute('data-cloud-name') || '';
    this.uploadPreset = root.getAttribute('data-upload-preset') || '';
    this.maxImages = parseInt(root.getAttribute('data-max-images'), 10) || 3;
    this.maxFileSizeMb = parseFloat(root.getAttribute('data-max-file-size-mb')) || 15;
    this.maxDimension = parseInt(root.getAttribute('data-max-dimension'), 10) || 2000;
    this.compress = root.getAttribute('data-compress') !== 'false';
    this.storageKey =
      'contactUploader:' + (root.getAttribute('data-form-id') || 'ContactForm');
 
    // ---- 文言（Liquid の data-* から。未指定はデフォルト） ----
    this.txt = {
      tooMany: root.getAttribute('data-txt-too-many') || '画像は最大 {n} 枚までです。',
      notImage: root.getAttribute('data-txt-not-image') || '画像ファイルを選択してください。',
      tooLarge:
        root.getAttribute('data-txt-too-large') ||
        'ファイルサイズが大きすぎます（上限 {n}MB）。',
      uploadError:
        root.getAttribute('data-txt-upload-error') ||
        'アップロードに失敗しました。もう一度お試しください。',
      uploading: root.getAttribute('data-txt-uploading') || 'アップロード中…',
      remove: root.getAttribute('data-txt-remove') || '削除',
    };
 
    // ---- DOM 参照 ----
    this.input = root.querySelector('[data-uploader-input]');
    this.list = root.querySelector('[data-uploader-list]');
    this.counter = root.querySelector('[data-uploader-counter]');
    this.hidden = root.querySelector('[data-uploader-hidden]');
    this.error = root.querySelector('[data-uploader-error]');
    this.form = root.closest('form');
    this.submitBtn = this.form
      ? this.form.querySelector('button[type="submit"], [type="submit"]')
      : null;
 
    // 設定不足なら無効化（フォーム自体は通常どおり動く）
    if (!this.cloudName || !this.uploadPreset) {
      console.warn(
        '[contact-uploader] Cloudinary の cloud name / upload preset が未設定のため画像アップロードは無効です。'
      );
      root.hidden = true;
      return;
    }
 
    this.items = []; // { id, status:'uploading'|'done'|'error', url, progress, thumb }
    this.seq = 0;
    this.endpoint =
      'https://api.cloudinary.com/v1_1/' + this.cloudName + '/image/upload';
 
    this._bind();
    this._restore();
    this._render();
  }
 
  ContactImageUploader.prototype._bind = function () {
    var self = this;
 
    // 「画像を追加」ボタン → 隠しファイル入力を開く
    var trigger = this.root.querySelector('[data-uploader-trigger]');
    if (trigger) {
      trigger.addEventListener('click', function () {
        if (!trigger.disabled) self.input.click();
      });
    }
 
    this.input.addEventListener('change', function (e) {
      var files = Array.prototype.slice.call(e.target.files || []);
      self._addFiles(files);
      // 同じファイルを選び直せるように毎回リセット
      self.input.value = '';
    });
 
    // クリック委譲（削除ボタン）
    this.list.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-remove-id]');
      if (!btn) return;
      e.preventDefault();
      self._remove(btn.getAttribute('data-remove-id'));
    });
 
    // 送信成功時（Liquid が data-posted="true" を付ける）に保存内容をクリア
    if (this.root.getAttribute('data-posted') === 'true') {
      this._clearStorage();
    }
  };
 
  /** 追加されたファイル群を検証・登録・アップロード */
  ContactImageUploader.prototype._addFiles = function (files) {
    var self = this;
    this._setError('');
 
    files.forEach(function (file) {
      if (self._activeCount() >= self.maxImages) {
        self._setError(self.txt.tooMany.replace('{n}', self.maxImages));
        return;
      }
      if (!/^image\//.test(file.type)) {
        self._setError(self.txt.notImage);
        return;
      }
      if (file.size > self.maxFileSizeMb * 1024 * 1024) {
        self._setError(self.txt.tooLarge.replace('{n}', self.maxFileSizeMb));
        return;
      }
 
      var item = {
        id: 'img' + ++self.seq,
        status: 'uploading',
        url: '',
        progress: 0,
        thumb: '',
      };
      self.items.push(item);
      self._render();
 
      // プレビュー用サムネイル（元ファイルから）
      self._makeThumb(file, function (thumbUrl) {
        item.thumb = thumbUrl;
        self._render();
      });
 
      // リサイズ→アップロード
      self._prepare(file, function (blob) {
        self._upload(item, blob);
      });
    });
  };
 
  /** 有効枚数（error を除く） */
  ContactImageUploader.prototype._activeCount = function () {
    return this.items.filter(function (i) {
      return i.status !== 'error';
    }).length;
  };
 
  /** リサイズ/圧縮（compress=false なら元ファイルをそのまま） */
  ContactImageUploader.prototype._prepare = function (file, done) {
    var self = this;
    if (!this.compress) {
      done(file);
      return;
    }
    this._loadBitmap(file, function (bmp) {
      if (!bmp) {
        done(file); // デコード不可時は元ファイル
        return;
      }
      var w = bmp.width,
        h = bmp.height;
      var scale = Math.min(1, self.maxDimension / Math.max(w, h));
      var tw = Math.round(w * scale),
        th = Math.round(h * scale);
 
      var canvas = document.createElement('canvas');
      canvas.width = tw;
      canvas.height = th;
      var ctx = canvas.getContext('2d');
      ctx.drawImage(bmp, 0, 0, tw, th);
      if (bmp.close) bmp.close();
 
      canvas.toBlob(
        function (blob) {
          done(blob || file);
        },
        'image/jpeg',
        0.85
      );
    });
  };
 
  /** EXIF回転を反映してデコード（createImageBitmap 優先、fallback あり） */
  ContactImageUploader.prototype._loadBitmap = function (file, cb) {
    if (
      'createImageBitmap' in window &&
      typeof window.createImageBitmap === 'function'
    ) {
      window
        .createImageBitmap(file, { imageOrientation: 'from-image' })
        .then(function (bmp) {
          cb(bmp);
        })
        .catch(function () {
          // 一部ブラウザは options 非対応 → オプション無しで再試行
          window
            .createImageBitmap(file)
            .then(function (bmp) {
              cb(bmp);
            })
            .catch(function () {
              cb(null);
            });
        });
    } else {
      var img = new Image();
      var url = URL.createObjectURL(file);
      img.onload = function () {
        URL.revokeObjectURL(url);
        cb(img);
      };
      img.onerror = function () {
        URL.revokeObjectURL(url);
        cb(null);
      };
      img.src = url;
    }
  };
 
  /** プレビュー用の小さなサムネイルURLを生成 */
  ContactImageUploader.prototype._makeThumb = function (file, cb) {
    var url = URL.createObjectURL(file);
    cb(url); // object URL をそのままプレビューに使用（remove 時に revoke）
  };
 
  /** Cloudinary へXHRでアップロード（進捗表示付き） */
  ContactImageUploader.prototype._upload = function (item, blob) {
    var self = this;
    var xhr = new XMLHttpRequest();
    item._xhr = xhr;
    xhr.open('POST', this.endpoint, true);
 
    xhr.upload.onprogress = function (e) {
      if (e.lengthComputable) {
        item.progress = Math.round((e.loaded / e.total) * 100);
        self._renderProgress(item);
      }
    };
 
    xhr.onload = function () {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          var res = JSON.parse(xhr.responseText);
          item.url = res.secure_url || res.url || '';
          item.status = item.url ? 'done' : 'error';
        } catch (err) {
          item.status = 'error';
        }
      } else {
        item.status = 'error';
      }
      if (item.status === 'error') self._setError(self.txt.uploadError);
      self._sync();
      self._render();
    };
 
    xhr.onerror = function () {
      item.status = 'error';
      self._setError(self.txt.uploadError);
      self._sync();
      self._render();
    };
 
    var fd = new FormData();
    fd.append('file', blob);
    fd.append('upload_preset', this.uploadPreset);
    xhr.send(fd);
 
    this._sync(); // 送信ボタンを一旦ロック
  };
 
  ContactImageUploader.prototype._remove = function (id) {
    var idx = this.items.findIndex(function (i) {
      return i.id === id;
    });
    if (idx === -1) return;
    var item = this.items[idx];
    if (item._xhr && item.status === 'uploading') {
      try {
        item._xhr.abort();
      } catch (e) {}
    }
    if (item.thumb && item.thumb.indexOf('blob:') === 0) {
      URL.revokeObjectURL(item.thumb);
    }
    this.items.splice(idx, 1);
    this._setError('');
    this._sync();
    this._render();
  };
 
  /** 隠しフィールド更新・送信ボタン制御・保存 */
  ContactImageUploader.prototype._sync = function () {
    var urls = this.items
      .filter(function (i) {
        return i.status === 'done' && i.url;
      })
      .map(function (i) {
        return i.url;
      });
 
    // 番号付きで改行連結（メール本文で見やすく）
    if (this.hidden) {
      if (urls.length) {
        this.hidden.value = urls
          .map(function (u, i) {
            return i + 1 + '. ' + u;
          })
          .join('\n');
        this.hidden.disabled = false; // 値がある時だけ送信対象にする
      } else {
        this.hidden.value = '';
        this.hidden.disabled = true; // 空フィールドを通知メールに出さない
      }
    }
 
    // アップロード中は送信ボタンを無効化
    var uploading = this.items.some(function (i) {
      return i.status === 'uploading';
    });
    if (this.submitBtn) {
      this.submitBtn.disabled = uploading;
      this.submitBtn.setAttribute('aria-busy', uploading ? 'true' : 'false');
    }
 
    this._save(urls);
  };
 
  /** ---- 表示 ---- */
  ContactImageUploader.prototype._render = function () {
    var self = this;
    // カウンタ
    if (this.counter) {
      this.counter.textContent = this._activeCount() + ' / ' + this.maxImages;
    }
    // 追加ボタン（input）の到達可否
    var full = this._activeCount() >= this.maxImages;
    var trigger = this.root.querySelector('[data-uploader-trigger]');
    if (trigger) {
      trigger.disabled = full;
      trigger.setAttribute('aria-disabled', full ? 'true' : 'false');
    }
 
    // リスト再描画
    this.list.innerHTML = '';
    this.items.forEach(function (item) {
      var li = document.createElement('li');
      li.className = 'cf-uploader__item cf-uploader__item--' + item.status;
      li.setAttribute('data-item-id', item.id);
 
      var thumb = document.createElement('span');
      thumb.className = 'cf-uploader__thumb';
      if (item.thumb) {
        var img = document.createElement('img');
        img.src = item.thumb;
        img.alt = '';
        thumb.appendChild(img);
      }
      li.appendChild(thumb);
 
      var meta = document.createElement('span');
      meta.className = 'cf-uploader__meta';
      if (item.status === 'uploading') {
        meta.innerHTML =
          '<span class="cf-uploader__status">' +
          self.txt.uploading +
          '</span><span class="cf-uploader__bar"><span class="cf-uploader__bar-fill" style="width:' +
          (item.progress || 0) +
          '%"></span></span>';
      } else if (item.status === 'error') {
        meta.innerHTML =
          '<span class="cf-uploader__status cf-uploader__status--error">×</span>';
      } else {
        meta.innerHTML =
          '<span class="cf-uploader__status cf-uploader__status--done">✓</span>';
      }
      li.appendChild(meta);
 
      var rm = document.createElement('button');
      rm.type = 'button';
      rm.className = 'cf-uploader__remove';
      rm.setAttribute('data-remove-id', item.id);
      rm.setAttribute('aria-label', self.txt.remove);
      rm.textContent = '×';
      li.appendChild(rm);
 
      self.list.appendChild(li);
    });
  };
 
  ContactImageUploader.prototype._renderProgress = function (item) {
    var li = this.list.querySelector('[data-item-id="' + item.id + '"]');
    if (!li) return;
    var fill = li.querySelector('.cf-uploader__bar-fill');
    if (fill) fill.style.width = (item.progress || 0) + '%';
  };
 
  ContactImageUploader.prototype._setError = function (msg) {
    if (this.error) {
      this.error.textContent = msg || '';
      this.error.hidden = !msg;
    }
  };
 
  /** ---- sessionStorage（エラー再読込時にURLを復元） ---- */
  ContactImageUploader.prototype._save = function (urls) {
    try {
      if (urls && urls.length) {
        sessionStorage.setItem(this.storageKey, JSON.stringify(urls));
      } else {
        sessionStorage.removeItem(this.storageKey);
      }
    } catch (e) {}
  };
 
  ContactImageUploader.prototype._restore = function () {
    var raw;
    try {
      raw = sessionStorage.getItem(this.storageKey);
    } catch (e) {
      return;
    }
    if (!raw) return;
    var urls;
    try {
      urls = JSON.parse(raw);
    } catch (e) {
      return;
    }
    if (!Array.isArray(urls)) return;
    var self = this;
    urls.slice(0, this.maxImages).forEach(function (u) {
      self.items.push({
        id: 'img' + ++self.seq,
        status: 'done',
        url: u,
        progress: 100,
        thumb: u, // アップロード済みURLをそのままサムネイルに
      });
    });
    this._sync();
  };
 
  ContactImageUploader.prototype._clearStorage = function () {
    try {
      sessionStorage.removeItem(this.storageKey);
    } catch (e) {}
  };
 
  // 初期化
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  // テーマエディターでのセクション再読込に対応
  document.addEventListener('shopify:section:load', initAll);
})();